import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Crisp } from 'crisp-sdk-web';
import { useAuth } from '../contexts/AuthContext';
import { log } from '../lib/logger';

/**
 * Mounts the Crisp chat widget once per app session and keeps it in sync
 * with the currently-logged-in user. Gated by REACT_APP_CRISP_WEBSITE_ID —
 * if the env var is unset the widget never loads (no network, no global
 * side-effects), so local/dev environments stay clean.
 *
 * Drop `<CrispChat />` once near the root of App.js.
 *
 * iter114c — branding alignment:
 *   - Theme switched from default Crisp blue → `green` to match
 *     EG3's emerald palette.
 *   - On chatbox-open we push a custom guided-expert greeting in
 *     place of the dashboard-configured "How can we help with EG3?"
 *     line. The message is shown ONCE per session so returning users
 *     don't get spammed every time they re-open the panel.
 */
const GUIDED_EXPERT_GREETING =
  "Tell us what you're trying to create, and we'll help match you with the best " +
  "product, material, size, and print method for your application — including " +
  'options that ship fastest and deliver the best value.';

const CrispChat = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const websiteId = process.env.REACT_APP_CRISP_WEBSITE_ID;

  // Collapse the 6 individual user fields into ONE memoized identity
  // payload so the sync effect only re-runs when the customer actually
  // logs in / out / updates their profile — not on every render.
  const identity = useMemo(() => {
    if (!isAuthenticated || !user?.email) return null;
    const nick = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    return {
      email: user.email,
      nickname: nick || null,
      data: {
        user_id: user.id || '',
        pricing_tier: user.pricing_tier || 'stickeryou',
        company: user.company || '',
      },
    };
  }, [isAuthenticated, user?.id, user?.email, user?.first_name, user?.last_name, user?.pricing_tier, user?.company]);

  // Per-session flag — guarantees the guided-expert greeting fires
  // exactly once per page lifetime regardless of how many times the
  // customer opens / closes the panel.
  const greetingShownRef = useRef(false);

  // 1) Init once on mount. Crisp.configure() is idempotent so the
  // double-invoke under React StrictMode is safe.
  useEffect(() => {
    if (!websiteId) return;
    try {
      Crisp.configure(websiteId, {
        autoload: true,
        sessionMerge: true,
      });
      // EG3 light-green chatbox theme — overrides Crisp's default
      // blue. Themes are predefined on Crisp's side; "green" maps to
      // a hue close to our `#4A7C59` brand colour. Set after configure
      // so it takes effect immediately on first render.
      Crisp.setColorTheme('green');

      // Hook chat-opened: push our guided-expert greeting once per
      // session. The dashboard-configured welcome line still shows
      // beneath this when present; ours appears as a fresh operator
      // bubble that nudges customers towards a sales-driven
      // conversation rather than a dead-end "How can we help" prompt.
      try {
        Crisp.chat.onChatOpened(() => {
          if (greetingShownRef.current) return;
          try {
            Crisp.message.show('text', GUIDED_EXPERT_GREETING);
            greetingShownRef.current = true;
          } catch (showErr) {
            log.debug('Crisp greeting show skipped:', showErr?.message || showErr);
          }
        });
      } catch (hookErr) {
        log.debug('Crisp onChatOpened hook unavailable:', hookErr?.message || hookErr);
      }
    } catch (err) {
      log.warn('Crisp init failed (widget disabled):', err);
    }
  }, [websiteId]);

  // 2) Sync authenticated user identity to Crisp so support agents see
  // the real customer email + name in their inbox.
  useEffect(() => {
    if (!websiteId) return;
    try {
      if (identity) {
        Crisp.user.setEmail(identity.email);
        if (identity.nickname) Crisp.user.setNickname(identity.nickname);
        Crisp.session.setData(identity.data);
      } else {
        // On logout, reset so the next user on the same device gets a
        // fresh conversation and doesn't inherit the old thread.
        Crisp.setTokenId(null);
        Crisp.session.reset();
      }
    } catch (err) {
      log.warn('Crisp identity sync failed:', err);
    }
  }, [websiteId, identity]);

  // 3) Track page-context on every route change so support agents see
  //    "what page were they on?" the moment a chat opens. Skipped when
  //    Crisp isn't configured (no-op).
  useEffect(() => {
    if (!websiteId) return;
    try {
      Crisp.session.setData({ current_page: location.pathname });
    } catch (err) {
      log.debug('Crisp page tracking skipped:', err?.message || err);
    }
  }, [websiteId, location.pathname]);

  return null;
};

export default CrispChat;
